import { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import GridViewIcon from '@mui/icons-material/GridView';
import TimelineIcon from '@mui/icons-material/Timeline';
import LinkIcon from '@mui/icons-material/Link';
import { useParams } from '@tanstack/react-router';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  useAddWorldElement,
  useAssociateWorldToBook,
  useReorderWorldElement,
  useWorld,
  WORLD_ELEMENT_TYPES,
  type WorldElementDto,
} from '../api/queries/worlds';
import { useBooks, type BookDto } from '../api/queries/books';
import { FilterableSelect } from '../components/common/FilterableSelect';
import { WorldElementEditor } from '../components/worlds/WorldElementEditor';
import { WorldCanvas } from '../components/worlds/WorldCanvas';
import { WorldTimeline } from '../components/worlds/WorldTimeline';
import { GenerationModal } from '../components/ai/GenerationModal';
import { ImportFromLibraryDialog } from '../components/taxonomy/ImportFromLibraryDialog';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import ViewModuleIcon from '@mui/icons-material/ViewModule';

/**
 * World Builder tree editor. Each sibling group is its own SortableContext, so drag
 * within a group reorders (ReorderWorldElementCommand). Cross-group reparent still
 * ships as the "Move to root" button — a drag-across-parents interaction is doable
 * but wants real drop indicators to feel right; deferred to a follow-up.
 */
export function WorldBuilderRoute() {
  const { worldId } = useParams({ strict: false }) as { worldId: string };
  const world = useWorld(worldId);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [importLibOpen, setImportLibOpen] = useState(false);
  const [viewMode, setViewMode] = useState<
    'tree' | 'canvas' | 'library' | 'timeline'
  >('tree');
  const [linkBookOpen, setLinkBookOpen] = useState(false);

  const selected =
    world.data?.elements.find((e) => e.id === selectedId) ?? null;

  const tree = useMemo(() => {
    if (!world.data) return new Map<string | null, WorldElementDto[]>();
    const byParent = new Map<string | null, WorldElementDto[]>();
    for (const e of world.data.elements) {
      const key = e.parentId ?? null;
      const arr = byParent.get(key) ?? [];
      arr.push(e);
      byParent.set(key, arr);
    }
    for (const arr of byParent.values())
      arr.sort((a, b) => a.sequenceOrder - b.sequenceOrder);
    return byParent;
  }, [world.data]);

  if (world.isLoading) return <CircularProgress />;
  if (world.isError || !world.data)
    return <Typography>Could not load world.</Typography>;

  const elementNames = world.data.elements.map((e) => e.name).join(', ');
  const suggestPrompt = [
    'Based on this world and its existing elements, suggest 3-5 new world elements that would enhance and deepen the worldbuilding.',
    'For each suggestion, provide a name, type, and brief description.',
    '',
    `World: ${world.data.name}`,
    world.data.description ? `Description: ${world.data.description}` : '',
    elementNames
      ? `Existing elements: ${elementNames}`
      : 'No elements exist yet.',
  ]
    .filter(Boolean)
    .join('\n');

  return (
    <Stack spacing={2} sx={{ minHeight: '80vh' }}>
      {/* Toolbar */}
      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography variant="h6" sx={{ flex: 1 }}>
          {world.data.name}
        </Typography>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, v) => {
            if (v) setViewMode(v);
          }}
          size="small"
        >
          <ToggleButton value="tree" aria-label="tree view">
            <AccountTreeIcon sx={{ mr: 0.5 }} fontSize="small" />
            Tree
          </ToggleButton>
          <ToggleButton value="canvas" aria-label="canvas view">
            <GridViewIcon sx={{ mr: 0.5 }} fontSize="small" />
            Canvas
          </ToggleButton>
          <ToggleButton value="library" aria-label="library view">
            <ViewModuleIcon sx={{ mr: 0.5 }} fontSize="small" />
            Library
          </ToggleButton>
          <ToggleButton value="timeline" aria-label="timeline view">
            <TimelineIcon sx={{ mr: 0.5 }} fontSize="small" />
            Timeline
          </ToggleButton>
        </ToggleButtonGroup>
        {/* Book link button */}
        <Button
          size="small"
          variant="outlined"
          startIcon={<LinkIcon />}
          onClick={() => setLinkBookOpen(true)}
        >
          Link to Book
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AutoFixHighIcon />}
          onClick={() => setSuggestOpen(true)}
        >
          Suggest
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<LibraryBooksIcon />}
          onClick={() => setImportLibOpen(true)}
        >
          Import
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => setAddOpen(true)}
        >
          + Element
        </Button>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ flex: 1 }}>
        {/* Left panel: Tree, Canvas, or Library */}
        {viewMode === 'tree' ? (
          <Paper sx={{ width: 360, p: 2, overflow: 'auto' }}>
            {world.data.description && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mb: 2 }}
              >
                {world.data.description}
              </Typography>
            )}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1 }}
            >
              {world.data.elements.length} element
              {world.data.elements.length === 1 ? '' : 's'} — drag within a
              group to reorder
            </Typography>
            <SortableTree
              parentId={null}
              tree={tree}
              selectedId={selectedId}
              onSelect={setSelectedId}
              worldId={worldId}
            />
          </Paper>
        ) : viewMode === 'canvas' ? (
          <WorldCanvas
            elements={world.data.elements}
            selectedElementId={selectedId}
            onSelectElement={setSelectedId}
          />
        ) : viewMode === 'timeline' ? (
          <WorldTimeline elements={world.data.elements} />
        ) : (
          <LibraryGridView
            elements={world.data.elements}
            selectedId={selectedId}
            onSelect={setSelectedId}
          />
        )}

        {/* Right panel: Element detail editor (not shown in library/timeline full-view) */}
        {viewMode !== 'library' && viewMode !== 'timeline' && (
          <Paper sx={{ flex: 1, p: 3 }}>
            {selected ? (
              <WorldElementEditor
                key={selected.id}
                worldId={worldId}
                element={selected}
                onUpdate={() => {
                  /* mutations handled inside the editor */
                }}
                onGenerateImage={() => {
                  /* image generation handled via GenerationModal inside editor */
                }}
              />
            ) : (
              <Typography color="text.secondary">
                Select an element from the tree to edit. Click "+ Element" to
                add a new one.
              </Typography>
            )}
          </Paper>
        )}
        {viewMode === 'library' && selected && (
          <Paper sx={{ width: 480, p: 3, overflow: 'auto' }}>
            <WorldElementEditor
              key={selected.id}
              worldId={worldId}
              element={selected}
              onUpdate={() => {}}
              onGenerateImage={() => {}}
            />
          </Paper>
        )}
      </Stack>

      <AddElementDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        worldId={worldId}
        parentId={selected?.id ?? null}
      />

      {/* AI Suggest Elements modal */}
      <GenerationModal
        open={suggestOpen}
        onClose={() => setSuggestOpen(false)}
        mode="text"
        seedPrompt={suggestPrompt}
      />

      {/* Import from Reference Library */}
      <ImportFromLibraryDialog
        open={importLibOpen}
        onClose={() => setImportLibOpen(false)}
        worldId={worldId}
      />

      {/* Link to Book dialog */}
      <LinkBookDialog
        open={linkBookOpen}
        onClose={() => setLinkBookOpen(false)}
        worldId={worldId}
      />
    </Stack>
  );
}

function LibraryGridView({
  elements,
  selectedId,
  onSelect,
}: {
  elements: WorldElementDto[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const [typeFilter, setTypeFilter] = useState<string>('');

  // Compute type counts
  const typeCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const el of elements) {
      counts.set(el.elementType, (counts.get(el.elementType) ?? 0) + 1);
    }
    return counts;
  }, [elements]);

  const filteredElements = typeFilter
    ? elements.filter((el) => el.elementType === typeFilter)
    : elements;

  // Extract card stats from metadata for display
  const getCardStats = (el: WorldElementDto): string[] => {
    const md = el.metadata as Record<string, unknown> | null;
    if (!md) return [];
    const card = (md.card ?? md) as Record<string, unknown>;
    const stats: string[] = [];
    if (card.tier) stats.push(`T${card.tier}`);
    if (card.damage && card.damage !== 'none') stats.push(`${card.damage} dmg`);
    if (card.ether_cost) stats.push(`${card.ether_cost} ether`);
    if (card.danger_tier) stats.push(`Danger ${card.danger_tier}`);
    if (card.damage_type) stats.push(String(card.damage_type));
    if (card.alignment) stats.push(String(card.alignment));
    return stats.slice(0, 3);
  };

  return (
    <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
      {/* Type filter chips */}
      <Stack
        direction="row"
        spacing={0.5}
        sx={{ mb: 1.5, flexWrap: 'wrap', gap: 0.5 }}
      >
        <Chip
          label={`All (${elements.length})`}
          size="small"
          variant={typeFilter === '' ? 'filled' : 'outlined'}
          color={typeFilter === '' ? 'primary' : 'default'}
          onClick={() => setTypeFilter('')}
        />
        {Array.from(typeCounts.entries())
          .sort((a, b) => b[1] - a[1])
          .map(([t, count]) => (
            <Chip
              key={t}
              label={`${t.replace(/_/g, ' ')} (${count})`}
              size="small"
              variant={typeFilter === t ? 'filled' : 'outlined'}
              color={typeFilter === t ? 'primary' : 'default'}
              onClick={() => setTypeFilter(t === typeFilter ? '' : t)}
            />
          ))}
      </Stack>

      {/* Card grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: 1.5,
        }}
      >
        {filteredElements.map((el) => {
          const stats = getCardStats(el);
          return (
            <Paper
              key={el.id}
              variant="outlined"
              sx={{
                p: 1.5,
                cursor: 'pointer',
                borderColor: selectedId === el.id ? 'primary.main' : 'divider',
                borderWidth: selectedId === el.id ? 2 : 1,
                transition: 'box-shadow 0.15s',
                '&:hover': { boxShadow: 2 },
              }}
              onClick={() => onSelect(el.id)}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography
                  variant="subtitle2"
                  noWrap
                  sx={{ flex: 1 }}
                  fontWeight={600}
                >
                  {el.name}
                </Typography>
                <Chip
                  size="small"
                  label={el.elementType.replace(/_/g, ' ')}
                  variant="outlined"
                  sx={{ fontSize: 11 }}
                />
              </Stack>
              {el.description && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mt: 0.5,
                    fontSize: 12,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {el.description.split('\n')[0]}
                </Typography>
              )}
              {stats.length > 0 && (
                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}
                >
                  {stats.map((s) => (
                    <Chip
                      key={s}
                      label={s}
                      size="small"
                      variant="outlined"
                      color="info"
                      sx={{ fontSize: 11, height: 20 }}
                    />
                  ))}
                </Stack>
              )}
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
}

function LinkBookDialog({
  open,
  onClose,
  worldId,
}: {
  open: boolean;
  onClose: () => void;
  worldId: string;
}) {
  const books = useBooks();
  const link = useAssociateWorldToBook();
  const [selectedBook, setSelectedBook] = useState<BookDto | null>(null);

  const options = books.data ?? [];

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Link to Book</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Select a book to link this world to. The world will appear in that
            book&apos;s World Builder panel.
          </Typography>
          {books.isLoading && <CircularProgress size={24} />}
          <Autocomplete
            options={options}
            getOptionLabel={(opt) => opt.title}
            loading={books.isLoading}
            value={selectedBook}
            onChange={(_, val) => setSelectedBook(val)}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Stack>
                  <Typography variant="body2">{option.title}</Typography>
                  {option.genre && (
                    <Typography variant="caption" color="text.secondary">
                      {option.genre}
                    </Typography>
                  )}
                </Stack>
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search books"
                placeholder="Type to search..."
              />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!selectedBook || link.isPending}
          onClick={async () => {
            if (!selectedBook) return;
            await link.mutateAsync({
              bookId: selectedBook.id,
              worldId,
            });
            setSelectedBook(null);
            onClose();
          }}
        >
          {link.isPending ? 'Linking...' : 'Link'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SortableTree({
  parentId,
  tree,
  selectedId,
  onSelect,
  worldId,
  depth = 0,
}: {
  parentId: string | null;
  tree: Map<string | null, WorldElementDto[]>;
  selectedId: string | null;
  onSelect: (id: string) => void;
  worldId: string;
  depth?: number;
}) {
  const items = tree.get(parentId) ?? [];
  const reorder = useReorderWorldElement(worldId);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  if (items.length === 0) return null;

  const ids = items.map((e) => e.id);

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(String(active.id));
    const newIndex = ids.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0) return;
    // Optimistic in-memory shuffle to smooth visual jank; TanStack Query refetch from
    // the mutation's invalidation is what actually commits the new order.
    void arrayMove(items, oldIndex, newIndex);
    reorder.mutate({ id: String(active.id), newSequenceOrder: newIndex });
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        {items.map((el) => (
          <Box key={el.id}>
            <SortableRow
              element={el}
              selected={el.id === selectedId}
              onSelect={onSelect}
              depth={depth}
            />
            <SortableTree
              parentId={el.id}
              tree={tree}
              selectedId={selectedId}
              onSelect={onSelect}
              worldId={worldId}
              depth={depth + 1}
            />
          </Box>
        ))}
      </SortableContext>
    </DndContext>
  );
}

function SortableRow({
  element,
  selected,
  onSelect,
  depth,
}: {
  element: WorldElementDto;
  selected: boolean;
  onSelect: (id: string) => void;
  depth: number;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: element.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  return (
    <Box
      ref={setNodeRef}
      style={style}
      onClick={() => onSelect(element.id)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        pl: 2 + depth * 2,
        pr: 1,
        py: 0.5,
        cursor: 'pointer',
        bgcolor: selected ? 'action.selected' : 'transparent',
        ':hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{
          mr: 1,
          cursor: 'grab',
          color: 'text.secondary',
          userSelect: 'none',
        }}
        aria-label="drag handle"
        onClick={(e) => e.stopPropagation()}
      >
        ⋮⋮
      </Box>
      <Typography variant="body2" sx={{ flex: 1 }} noWrap>
        {element.name}
      </Typography>
      <Chip size="small" label={element.elementType} />
    </Box>
  );
}

function AddElementDialog({
  open,
  onClose,
  worldId,
  parentId,
}: {
  open: boolean;
  onClose: () => void;
  worldId: string;
  parentId: string | null;
}) {
  const add = useAddWorldElement(undefined);
  const [name, setName] = useState('');
  const [type, setType] = useState(WORLD_ELEMENT_TYPES[0].value);
  const [description, setDescription] = useState('');

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add element {parentId ? '(nested)' : ''}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
          <FilterableSelect
            label="Type"
            value={type}
            onChange={(newValue) =>
              setType(newValue || WORLD_ELEMENT_TYPES[0].value)
            }
            allowEmpty={false}
            options={WORLD_ELEMENT_TYPES.map((t) => ({
              value: t.value,
              label: t.label,
            }))}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!name.trim() || add.isPending}
          onClick={async () => {
            await add.mutateAsync({
              worldId,
              name: name.trim(),
              elementType: type,
              description,
            } as unknown as {
              worldId: string;
              name: string;
              elementType: string;
              description?: string;
            });
            setName('');
            setDescription('');
            onClose();
          }}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}
